import { BodyText, Button, Headline } from '@servicetitan/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { usePortalMode } from '../../../hooks/use-portal-mode';
import { SelectFieldState, SelectOption } from '../../../utils/form';
import { PortalPopover, PortalPopoverProps } from '../../modals/modals';
import { SearchInput } from './search-input';
import { SearchList } from './search-list';
import {
    PortalSelectTriggerInput,
    PortalSelectTriggerTag,
    PortalSelectTriggerOpts,
    PortalSelectTriggerProps,
} from './select-triggers';

export interface PortalSelectSearchProps {
    className?: string;
    allText?: string;
    noneText?: string;
    saveText?: string;
    placeholder?: string;
}

export interface PortalSelectProps<T, TD = never> {
    className?: string;
    title: string;
    mobile: boolean;
    disabled?: boolean;
    state: SelectFieldState<T, T | T[], TD>;
    trigger?: PortalSelectTriggerOpts;
    triggerComponent?: 'tag' | FC<PortalSelectTriggerProps<T, TD>>;
    popover?: PortalPopoverProps['popover'];
    takeover?: PortalPopoverProps['takeover'];
    hideSearch?: boolean;
    search?: PortalSelectSearchProps;
    optionComponent?: FC<{ opt: SelectOption<T, TD> }>;
    optionsFilter?(search: string): (opt: SelectOption<T, TD>) => boolean;

    /**
     * triggered when values are changed
     * in mobile mode it happens when user clicks apply button
     * in desktop mode it happens immediately when item checkbox is toggled
     */
    onValuesChanged?(values: T[]): void;

    /**
     * triggered when values are applied
     * in mobile mode it happens when user clicks apply button and there are some changes in selected values
     * in desktop mode it happens when popover is closed and there are some changes in selected values
     */
    onValuesApplied?(values: T[]): void;
}

/**
 * Replica of AnvilSelect extended for mobile usage
 * @param props PortalSelectProps
 */
export function PortalSelect<T extends number | string, TD>({
    className,
    disabled,
    hideSearch,
    mobile,
    onValuesApplied,
    onValuesChanged,
    optionComponent,
    optionsFilter,
    popover = {},
    search: searchOpts = {},
    state,
    takeover = {},
    title,
    trigger,
    triggerComponent,
}: PortalSelectProps<T, TD>) {
    const [searchOpen, setSearchOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedValues, setSelectedValues] = useState<T[]>([]);
    const [initialSelectedValues, setInitialSelectedValues] = useState<T[]>([]);
    const multiple = state.multiple;
    const areValuesChanged = useMemo(
        () =>
            selectedValues.some(i => !initialSelectedValues.includes(i)) ||
            initialSelectedValues.some(i => !selectedValues.includes(i)),
        [selectedValues, initialSelectedValues]
    );
    const closeSearch = useCallback(() => {
        setSearchOpen(false);

        if (onValuesApplied && areValuesChanged) {
            onValuesApplied(selectedValues);
        }
    }, [selectedValues, areValuesChanged, onValuesApplied]);
    const openSearch = useCallback(() => {
        if (disabled) {
            return;
        }
        const values = state.selectedOptions?.map(opt => opt.value) ?? [];
        setSelectedValues([...values]);
        setInitialSelectedValues([...values]);
        setSearch('');
        setSearchOpen(true);
    }, [state.selectedOptions, disabled]);
    const onChangeStateValues = useCallback(
        (values: T[]) => {
            state.onChangeHandler(
                values.length
                    ? values.map(v => ({ value: v, text: '', data: undefined as any }))
                    : undefined
            );

            if (onValuesChanged) {
                onValuesChanged(values);
            }
        },
        [onValuesChanged, state]
    );
    const onApply = useCallback(() => {
        onChangeStateValues(selectedValues);
        closeSearch();
    }, [closeSearch, onChangeStateValues, selectedValues]);
    const updateSelectedValues = useCallback(
        (values: T[]) => {
            setSelectedValues(values);

            if (!mobile || !multiple) {
                onChangeStateValues(values);
            }

            if (!multiple && !mobile) {
                // for mobile mode change value after Save button click
                closeSearch();
            }
        },
        [mobile, multiple, onChangeStateValues, closeSearch]
    );

    const selectAllValues = useCallback(() => {
        updateSelectedValues(state.flatOptions.map(opt => opt.value));
    }, [state, updateSelectedValues]);

    const selectNoneValues = useCallback(() => {
        updateSelectedValues([]);
    }, [updateSelectedValues]);

    const TriggerComponent =
        triggerComponent === 'tag'
            ? PortalSelectTriggerTag
            : (triggerComponent ?? PortalSelectTriggerInput);
    const triggerValue = (
        <TriggerComponent
            title={trigger?.title ?? title}
            opts={trigger}
            selectedOptions={state.selectedOptions as any}
            searchOpen={searchOpen}
            openSearch={openSearch}
            closeSearch={closeSearch}
            updateValues={updateSelectedValues}
            multiple={multiple}
            disabled={disabled}
        />
    );

    const { placeholder: searchPlaceholder = 'Search', saveText = 'Save' } = searchOpts;

    return (
        <div className={classNames(className)}>
            <PortalPopover
                title={title}
                mobile={mobile}
                trigger={triggerValue}
                onClose={closeSearch}
                popover={popover}
                takeover={{
                    theme: 'combined',
                    ...(takeover ?? {}),
                }}
                open={searchOpen}
                footerAlign={mobile ? 'space-between' : 'left'}
                thin
                footer={
                    mobile ? (
                        <Fragment>
                            <Button
                                className="flex-grow-1 flex-basis-0 m-r-1"
                                onClick={selectNoneValues}
                            >
                                Clear All
                            </Button>
                            <Button
                                className="flex-grow-1 flex-basis-0 m-l-1"
                                onClick={onApply}
                                disabled={!areValuesChanged}
                                primary
                            >
                                {saveText}
                                {multiple && selectedValues.length
                                    ? ` (${
                                          selectedValues.length === state.flatOptions.length
                                              ? 'All'
                                              : selectedValues.length
                                      })`
                                    : ''}
                            </Button>
                        </Fragment>
                    ) : multiple ? (
                        <Fragment>
                            <BodyText size="small" subdued>
                                Select
                            </BodyText>
                            <BodyText
                                size="small"
                                className="c-blue-500 cursor-pointer m-l-1"
                                onClick={selectAllValues}
                            >
                                All
                            </BodyText>
                            <BodyText
                                size="small"
                                className="c-blue-500 cursor-pointer m-l-1"
                                onClick={selectNoneValues}
                            >
                                None
                            </BodyText>
                        </Fragment>
                    ) : undefined
                }
            >
                {!mobile && !!title && (
                    <Headline className="m-t-2 m-x-3" size="small">
                        {title}
                    </Headline>
                )}
                {!hideSearch && (
                    <SearchInput
                        className={classNames(
                            'p-b-3 m-t-2 m-b-0',
                            mobile ? 'p-x-2' : 'p-x-3 border-bottom'
                        )}
                        search={search}
                        setSearch={setSearch}
                        placeholder={searchPlaceholder}
                    />
                )}
                <SearchList
                    search={search}
                    options={state.options}
                    selectedValues={selectedValues}
                    updateSelectedValues={updateSelectedValues}
                    selectAllValues={selectAllValues}
                    optionComponent={optionComponent}
                    optionsFilter={optionsFilter}
                    multiple={multiple}
                    mobile={mobile}
                    opts={searchOpts}
                />
            </PortalPopover>
        </div>
    );
}

function PortalSelectObserverContent<T extends number | string, TD>(
    props: Omit<PortalSelectProps<T, TD>, 'mobile'>
) {
    const mode = usePortalMode();

    return <PortalSelect mobile={mode.mobile} {...props} />;
}

/**
 * Replica of AnvilSelect extended for mobile usage
 * @param props PortalSelectProps
 */
export const PortalSelectObserver = observer(PortalSelectObserverContent);
